import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  buildCreateAdminProspectLetterTemplateRichVersionRequest,
  buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest,
  buildGetAdminProspectLetterTemplateRequest,
  buildListAdminProspectLetterTemplateVersionsRequest,
  createAdminProspectLetterTemplateVersion,
  createAdminProspectLetterTemplateWriteAttempt,
  getAdminProspectLetterTemplate,
  getAdminProspectLetterTemplateAssetPreviewUrl,
  listAdminProspectLetterTemplateVersions,
} from '../../services/admin/prospectLetterTemplatesService.js';
import {
  adminProspectLetterTemplateErrorKey,
  adminProspectLetterTemplateErrorKeyFromError,
  adminProspectLetterTemplateStatusBadgeClass,
  adminProspectLetterTemplateStatusLabel,
  classifyAdminCallableError,
  extractLetterTemplateBusinessError,
} from './adminProspectLetterTemplateDisplay.js';
import LetterTemplatePreview from './letterTemplates/LetterTemplatePreview.jsx';
import LetterTemplatePdfPreviewButton from './letterTemplates/LetterTemplatePdfPreviewButton.jsx';
import LetterTemplateRichEditorShell from './letterTemplates/LetterTemplateRichEditorShell.jsx';
import {
  buildLetterTemplateRichDraft,
  letterTemplateRichDraftsEqual,
} from './letterTemplates/letterTemplateBilingualState.js';
import {
  assertCallableSafeLetterTemplatePayload,
  cloneCallableSafeLetterTemplateJson,
  logLetterTemplateCreateDiagnostic,
} from '../../services/admin/letterTemplateCallableSafety.js';
import {
  confirmDiscardUnsavedDraft,
  letterTemplateWriteActionsEnabled,
  mergeLetterTemplateVersionRows,
  resolveVersionNavigation,
} from './letterTemplates/letterTemplateEditorSession.js';
import {
  initializeTranslationSession,
  shouldBlockSaveForStaleTranslation,
} from './letterTemplates/letterTemplateTranslationState.js';
import { useLetterTemplateAssetPreviewUrls } from './letterTemplates/useLetterTemplateAssetPreviewUrls.js';
import {
  createEmptyLetterTemplateDocument,
  extractLetterTemplateAssetIds,
  resolveBilingualFromVersion,
  resolveEnglishDocumentFromVersion,
  resolveFrenchDocumentFromVersion,
  resolveSchemaVersionFromDetail,
  validateLetterTemplateRichDraft,
} from './letterTemplates/letterTemplateDocument.js';
import AdminDuplicateLetterTemplateDialog from './AdminDuplicateLetterTemplateDialog.jsx';
import AdminArchiveLetterTemplateDialog from './AdminArchiveLetterTemplateDialog.jsx';

export default function AdminProspectLetterTemplateEditorPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { impersonation } = useAuth();
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [template, setTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionsNextPageToken, setVersionsNextPageToken] = useState(null);
  const [loadingOlderVersions, setLoadingOlderVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [frenchDocument, setFrenchDocument] = useState(null);
  const [englishDocument, setEnglishDocument] = useState(null);
  const [bilingual, setBilingual] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [translationSourceSnapshot, setTranslationSourceSnapshot] = useState(null);
  const [baselineDraft, setBaselineDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErrorKey, setSaveErrorKey] = useState(null);
  const [retryable, setRetryable] = useState(false);
  const [versionConflict, setVersionConflict] = useState(false);
  const [mobileTab, setMobileTab] = useState('edit');
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const versionAttemptRef = useRef(createAdminProspectLetterTemplateWriteAttempt());
  const submitLockRef = useRef(false);
  const requestSeqRef = useRef(0);

  const impersonating = impersonation?.active === true;
  const isArchived = template?.status === 'archived';
  const isHistorical =
    selectedVersion != null &&
    template?.currentVersion != null &&
    selectedVersion !== template.currentVersion;
  const readOnly = isArchived || isHistorical || impersonating || status !== 'ready';

  const currentDraft = buildLetterTemplateRichDraft({
    bilingual,
    frenchDocument: frenchDocument || createEmptyLetterTemplateDocument(),
    englishDocument,
    translation,
  });

  const dirty = baselineDraft && !letterTemplateRichDraftsEqual(currentDraft, baselineDraft);
  const writeActionsEnabled = letterTemplateWriteActionsEnabled({ dirty, impersonating });
  const translationStale = shouldBlockSaveForStaleTranslation(
    translation,
    frenchDocument,
    translationSourceSnapshot,
  );

  const confirmDiscardIfDirty = useCallback(() => {
    return confirmDiscardUnsavedDraft({
      dirty: Boolean(dirty),
      confirm: () =>
        window.confirm(t('admin.prospection.letterTemplate.editor.discardUnsaved')),
    });
  }, [dirty, t]);

  const applyDetail = useCallback((detail, versionNumber) => {
    const root = detail?.template || null;
    const version = detail?.version || null;
    const activeVersion = versionNumber ?? root?.currentVersion ?? version?.version;
    const frDoc = resolveFrenchDocumentFromVersion(version);
    const enDoc = resolveEnglishDocumentFromVersion(version);
    const isBilingual = resolveBilingualFromVersion(version);
    const loadedTranslation = version?.translation || null;
    const session = initializeTranslationSession(frDoc, loadedTranslation);

    setTemplate(root);
    setSelectedVersion(activeVersion);
    setFrenchDocument(frDoc);
    setEnglishDocument(isBilingual ? enDoc || createEmptyLetterTemplateDocument() : null);
    setBilingual(isBilingual);
    setTranslation(session.metadata);
    setTranslationSourceSnapshot(session.sourceSnapshot);
    setBaselineDraft(
      buildLetterTemplateRichDraft({
        bilingual: isBilingual,
        frenchDocument: frDoc,
        englishDocument: isBilingual ? enDoc || createEmptyLetterTemplateDocument() : null,
        translation: session.metadata,
      }),
    );
    versionAttemptRef.current.reset();
  }, []);

  const loadTemplate = useCallback(
    async (versionNumber, { mode = 'bootstrap' } = {}) => {
      const requestId = ++requestSeqRef.current;
      setStatus('loading');
      setErrorKind(null);
      setVersionConflict(false);
      try {
        if (mode === 'detail-only') {
          const detail = await getAdminProspectLetterTemplate(
            buildGetAdminProspectLetterTemplateRequest({
              templateId,
              version: versionNumber ?? undefined,
            }),
          );
          if (requestId !== requestSeqRef.current) return;
          applyDetail(detail, versionNumber);
          setStatus('ready');
          return;
        }

        const [detail, versionList] = await Promise.all([
          getAdminProspectLetterTemplate(
            buildGetAdminProspectLetterTemplateRequest({
              templateId,
              version: versionNumber ?? undefined,
            }),
          ),
          listAdminProspectLetterTemplateVersions(
            buildListAdminProspectLetterTemplateVersionsRequest({ templateId }),
          ),
        ]);
        if (requestId !== requestSeqRef.current) return;
        const versionRows = Array.isArray(versionList?.versions) ? versionList.versions : [];
        if (mode === 'refresh-current') {
          applyDetail(detail, null);
          setVersions((current) => mergeLetterTemplateVersionRows(current, versionRows));
        } else {
          applyDetail(detail, versionNumber);
          setVersions(versionRows);
          setVersionsNextPageToken(versionList?.nextPageToken || null);
        }
        setStatus('ready');
      } catch (err) {
        if (requestId !== requestSeqRef.current) return;
        setErrorKind(classifyAdminCallableError(err));
        setStatus('error');
      }
    },
    [templateId, applyDetail],
  );

  useEffect(() => {
    if (templateId) loadTemplate(null, { mode: 'bootstrap' });
  }, [templateId, loadTemplate]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  const { getPreviewState, requestPreview } = useLetterTemplateAssetPreviewUrls({
    fetchPreviewUrl: async (assetId) =>
      getAdminProspectLetterTemplateAssetPreviewUrl(
        buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest({ assetId }),
      ),
    enabled: !impersonating && status === 'ready',
  });

  const previewAssetIds = [
    ...extractLetterTemplateAssetIds(frenchDocument),
    ...(bilingual ? extractLetterTemplateAssetIds(englishDocument) : []),
  ];

  useEffect(() => {
    if (impersonating || status !== 'ready') return;
    for (const assetId of previewAssetIds) {
      requestPreview(assetId).catch(() => {});
    }
  }, [previewAssetIds, impersonating, status, requestPreview]);

  const editorSessionKey = `${templateId}:${selectedVersion ?? template?.currentVersion ?? 'current'}`;

  const getAssetPreviewUrl = (assetId) => {
    if (!assetId || impersonating) return null;
    const state = getPreviewState(assetId);
    return state.status === 'ready' ? state.url : null;
  };

  const handleVersionSelect = (versionNumber) => {
    const decision = resolveVersionNavigation({
      dirty: Boolean(dirty),
      targetVersion: versionNumber,
      selectedVersion,
      confirm: () =>
        window.confirm(t('admin.prospection.letterTemplate.editor.discardUnsaved')),
    });
    if (decision.action !== 'load') return;
    loadTemplate(decision.version, { mode: 'detail-only' });
  };

  const handleReturnToCurrent = () => {
    if (!confirmDiscardIfDirty()) return;
    loadTemplate(null, { mode: 'refresh-current' });
  };

  const handleLoadOlderVersions = async () => {
    if (!versionsNextPageToken || loadingOlderVersions) return;
    setLoadingOlderVersions(true);
    try {
      const versionList = await listAdminProspectLetterTemplateVersions(
        buildListAdminProspectLetterTemplateVersionsRequest({
          templateId,
          pageToken: versionsNextPageToken,
        }),
      );
      const versionRows = Array.isArray(versionList?.versions) ? versionList.versions : [];
      setVersions((current) => mergeLetterTemplateVersionRows(current, versionRows));
      setVersionsNextPageToken(versionList?.nextPageToken || null);
    } finally {
      setLoadingOlderVersions(false);
    }
  };

  const handleFrenchChange = (nextDocument) => {
    versionAttemptRef.current.reset();
    setFrenchDocument(nextDocument);
    setSaveErrorKey(null);
    setVersionConflict(false);
  };

  const handleEnglishChange = (nextDocument) => {
    versionAttemptRef.current.reset();
    setEnglishDocument(nextDocument);
    setSaveErrorKey(null);
    setVersionConflict(false);
  };

  const draftValidation = validateLetterTemplateRichDraft({
    bilingual,
    frenchDocument,
    englishDocument: bilingual ? englishDocument : null,
    translationStale,
  });

  const canSave =
    !impersonating &&
    !readOnly &&
    !saving &&
    dirty &&
    draftValidation.valid;

  const handleSaveVersion = async () => {
    if (!canSave || submitLockRef.current || !template) return;
    submitLockRef.current = true;
    setSaving(true);
    setSaveErrorKey(null);
    setRetryable(false);
    setVersionConflict(false);
    let stage = 'fingerprint';
    try {
      const safeFr = cloneCallableSafeLetterTemplateJson(frenchDocument);
      const safeEn = bilingual ? cloneCallableSafeLetterTemplateJson(englishDocument) : null;
      const documents = { fr: safeFr };
      if (bilingual && safeEn) documents.en = safeEn;

      stage = 'callable-safe-assert';
      const safety = assertCallableSafeLetterTemplatePayload({
        templateId,
        documents,
      });
      if (!safety.ok) {
        const err = new Error('letter_template_payload_not_callable_safe');
        err.code = 'letter_template_payload_not_callable_safe';
        throw err;
      }

      stage = 'resolve-attempt';
      const payload = versionAttemptRef.current.resolveAttemptPayload({
        fingerprint: JSON.stringify({
          templateId,
          baseVersion: template.currentVersion,
          bilingual,
          documents,
        }),
        build: (operationId) => {
          stage = 'build-request';
          const request = buildCreateAdminProspectLetterTemplateRichVersionRequest({
            operationId,
            templateId,
            baseVersion: template.currentVersion,
            bilingual,
            documents,
          });
          if (translation && !translationStale) {
            request.translation = translation;
          }
          return request;
        },
      });
      stage = 'callable-create';
      const result = await createAdminProspectLetterTemplateVersion(payload);
      versionAttemptRef.current.discard();
      await loadTemplate(result?.version ?? null, { mode: 'refresh-current' });
    } catch (err) {
      logLetterTemplateCreateDiagnostic({
        stage,
        error: err,
        safeReason: err?.details?.reason || null,
      });
      if (extractLetterTemplateBusinessError(err) === 'template_version_conflict') {
        setVersionConflict(true);
        setSaveErrorKey('admin.prospection.letterTemplate.error.versionConflict');
      } else {
        setSaveErrorKey(adminProspectLetterTemplateErrorKeyFromError(err));
        setRetryable(classifyAdminCallableError(err) === 'unavailable');
      }
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  if (status === 'loading') {
    return (
      <p className="text-sm text-slate-500" aria-live="polite">
        {t('admin.prospection.letterTemplate.editor.loading')}
      </p>
    );
  }

  if (status === 'error') {
    return (
      <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
        {t(adminProspectLetterTemplateErrorKey(errorKind))}
      </p>
    );
  }

  const schemaVersion = resolveSchemaVersionFromDetail(template, { schemaVersion: template?.schemaVersion });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{template?.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${adminProspectLetterTemplateStatusBadgeClass(template?.status)}`}
              >
                {adminProspectLetterTemplateStatusLabel(template?.status, t)}
              </span>
              <span>
                {t('admin.prospection.letterTemplate.editor.versionLabel').replace(
                  '{version}',
                  String(selectedVersion ?? template?.currentVersion ?? ''),
                )}
              </span>
              {schemaVersion >= 2 ? (
                <span className="text-xs text-slate-500">
                  {t('admin.prospection.letterTemplate.editor.schemaV2')}
                </span>
              ) : null}
              {dirty ? (
                <span className="font-medium text-amber-800">
                  {t('admin.prospection.letterTemplate.editor.unsaved')}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {writeActionsEnabled ? (
            <button
              type="button"
              onClick={() => setDuplicateOpen(true)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
            >
              {t('admin.prospection.letterTemplate.action.duplicate')}
            </button>
          ) : null}
          {!isArchived && writeActionsEnabled ? (
            <button
              type="button"
              onClick={() => setArchiveOpen(true)}
              className="rounded-lg border border-rose-300 px-3 py-1.5 text-sm text-rose-700"
            >
              {t('admin.prospection.letterTemplate.action.archive')}
            </button>
          ) : null}
        </div>
      </div>

      {isArchived ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
          {t('admin.prospection.letterTemplate.editor.archivedBanner')}
        </p>
      ) : null}
      {isHistorical ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
          <span>{t('admin.prospection.letterTemplate.editor.historicalReadOnly')}</span>
          <button
            type="button"
            onClick={handleReturnToCurrent}
            className="font-medium underline"
          >
            {t('admin.prospection.letterTemplate.editor.returnCurrent')}
          </button>
        </div>
      ) : null}
      {impersonating ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {t('admin.prospection.letterTemplate.impersonationBlocked')}
        </p>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-sm font-medium text-slate-700" htmlFor="version-history">
          {t('admin.prospection.letterTemplate.editor.versionHistory')}
        </label>
        <select
          id="version-history"
          value={selectedVersion ?? ''}
          onChange={(event) => handleVersionSelect(Number(event.target.value))}
          className="mt-1 w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          {versions.map((row) => (
            <option key={row.version} value={row.version}>
              {row.version === template?.currentVersion
                ? t('admin.prospection.letterTemplate.editor.versionCurrent').replace(
                    '{version}',
                    String(row.version),
                  )
                : t('admin.prospection.letterTemplate.editor.versionItem').replace(
                    '{version}',
                    String(row.version),
                  )}
            </option>
          ))}
        </select>
        {versionsNextPageToken ? (
          <button
            type="button"
            onClick={handleLoadOlderVersions}
            disabled={loadingOlderVersions}
            className="mt-2 text-sm font-medium text-indigo-700 underline disabled:opacity-50"
          >
            {loadingOlderVersions
              ? t('admin.prospection.letterTemplate.editor.loadingOlderVersions')
              : t('admin.prospection.letterTemplate.editor.loadOlderVersions')}
          </button>
        ) : null}
      </div>

      <div className="flex gap-2 md:hidden">
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm ${mobileTab === 'edit' ? 'bg-indigo-600 text-white' : 'bg-slate-100'}`}
          onClick={() => setMobileTab('edit')}
        >
          {t('admin.prospection.letterTemplate.mobile.edit')}
        </button>
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm ${mobileTab === 'preview' ? 'bg-indigo-600 text-white' : 'bg-slate-100'}`}
          onClick={() => setMobileTab('preview')}
        >
          {t('admin.prospection.letterTemplate.mobile.preview')}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className={mobileTab === 'preview' ? 'hidden lg:block' : ''}>
          <LetterTemplateRichEditorShell
            editorSessionKey={editorSessionKey}
            frenchDocument={frenchDocument}
            englishDocument={englishDocument}
            bilingual={bilingual}
            translation={translation}
            readOnly={readOnly}
            impersonating={impersonating}
            t={t}
            onFrenchDocumentChange={handleFrenchChange}
            onEnglishDocumentChange={handleEnglishChange}
            onBilingualChange={(next) => {
              versionAttemptRef.current.reset();
              setBilingual(next);
              if (!next) {
                setEnglishDocument(null);
                setTranslation(null);
                setTranslationSourceSnapshot(null);
              }
            }}
            onTranslationChange={(meta, snapshot) => {
              setTranslation(meta);
              setTranslationSourceSnapshot(snapshot);
            }}
          />
        </div>
        <div className={mobileTab === 'edit' ? 'hidden lg:block' : ''}>
          <LetterTemplatePreview
            frenchDocument={frenchDocument}
            englishDocument={englishDocument}
            bilingual={bilingual}
            t={t}
            getAssetPreviewUrl={getAssetPreviewUrl}
          />
        </div>
      </div>

      {saveErrorKey ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
          <p>{t(saveErrorKey)}</p>
          {versionConflict ? (
            <button
              type="button"
              className="mt-2 font-medium text-rose-800 underline"
              onClick={handleReturnToCurrent}
            >
              {t('admin.prospection.letterTemplate.editor.reloadCurrent')}
            </button>
          ) : null}
          {retryable && !versionConflict ? (
            <button
              type="button"
              className="mt-2 font-medium text-rose-800 underline"
              onClick={handleSaveVersion}
            >
              {t('admin.prospection.letterTemplate.action.retry')}
            </button>
          ) : null}
        </div>
      ) : null}

      {translationStale && !readOnly ? (
        <p className="text-sm text-amber-800" role="status">
          {t('admin.prospection.letterTemplate.error.translationStaleSave')}
        </p>
      ) : null}

      <LetterTemplatePdfPreviewButton
        t={t}
        impersonating={impersonating}
        bilingual={bilingual}
        frenchDocument={frenchDocument}
        englishDocument={englishDocument}
        translation={translation}
        translationStale={translationStale}
        draftValid={draftValidation.valid}
      />

      {!readOnly ? (
        <button
          type="button"
          onClick={handleSaveVersion}
          disabled={!canSave}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving
            ? t('admin.prospection.letterTemplate.editor.saving')
            : t('admin.prospection.letterTemplate.editor.saveVersion')}
        </button>
      ) : null}

      <AdminDuplicateLetterTemplateDialog
        open={duplicateOpen}
        sourceTemplate={template}
        sourceVersion={isHistorical ? selectedVersion : undefined}
        onClose={() => setDuplicateOpen(false)}
        onSuccess={(newId) => navigate(`/admin/prospection/letter-templates/${newId}`)}
      />
      <AdminArchiveLetterTemplateDialog
        open={archiveOpen}
        template={template}
        onClose={() => setArchiveOpen(false)}
        onSuccess={() => loadTemplate(null, { mode: 'bootstrap' })}
      />
    </div>
  );
}
